import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Avatar,
  AvatarGroup,
  Tooltip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  InputAdornment,
  FormHelperText,
  Zoom,
  Fade,
  Card,
  CardContent,
  Backdrop
} from '@mui/material';
import {
  CalendarToday,
  Add,
  CheckCircle,
  Cancel,
  Refresh,
  Search,
  ListAlt,
  ArrowBack,
  ArrowForward,
  Today,
  Save,
  Close,
  EventNote,
  Person
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameDay, 
  addMonths, 
  subMonths, 
  isWithinInterval, 
  parseISO, 
  getDay, 
  isAfter, 
  isBefore,
  startOfDay
} from 'date-fns';
import axios from 'axios';

import Sidebar from '../../../../components/sidebars/Sidebar_hotelAccount_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';

// Styled components
const CalendarDay = styled(Paper, {
  shouldForwardProp: (prop) => !['selected', 'hasEvent', 'isToday'].includes(prop),
})(({ theme, selected, hasEvent, isToday }) => ({
  padding: theme.spacing(1),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: selected ? theme.palette.primary.main : 
                   hasEvent ? theme.palette.action.hover : 
                   'transparent',
  color: selected ? theme.palette.primary.contrastText : 
         isToday ? theme.palette.primary.main : 
         'inherit',
  border: isToday ? `2px solid ${theme.palette.primary.main}` : 'none',
  fontWeight: isToday ? 'bold' : 'normal',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.hover,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'approved' ? theme.palette.success.main :
                   status === 'pending' ? theme.palette.warning.main :
                   status === 'rejected' ? theme.palette.error.main :
                   theme.palette.info.main,
  color: '#fff',
  fontWeight: 'bold',
}));

const LeaveCalendar = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [leaveStats, setLeaveStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    daysUsed: 0,
    daysRemaining: 30,
  });
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    department: 'all',
    search: '',
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departments, setDepartments] = useState([]);
  const [currentUser, setCurrentUser] = useState({
    phone: '',
    name: '',
    department: '',
    email: '',
    user_type: ''
  });
  
  // Leave request form state
  const [leaveForm, setLeaveForm] = useState({
    phone: '',
    leaveType: 'annual',
    startDate: null,
    endDate: null,
    reason: '',
    contactDuringLeave: '',
    handoverNotes: '',
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  
  const toggleSidebar = () => setSidebarOpen(prev => !prev);
  
  // Helper function to normalize dates (remove time component)
  const normalizeDate = (date) => {
    if (!date) return null;
    return startOfDay(new Date(date));
  };
  
  // Leave types configuration
  const leaveTypes = [
    { value: 'annual', label: 'Annual Leave', color: '#2196f3', maxDays: 20, description: 'Regular annual leave' },
    { value: 'sick', label: 'Sick Leave', color: '#f44336', maxDays: 10, description: 'Medical/sick leave' },
    { value: 'maternity', label: 'Maternity Leave', color: '#e91e63', maxDays: 90, description: 'Maternity leave' },
    { value: 'paternity', label: 'Paternity Leave', color: '#9c27b0', maxDays: 14, description: 'Paternity leave' },
    { value: 'unpaid', label: 'Unpaid Leave', color: '#ff9800', maxDays: null, description: 'Leave without pay' },
    { value: 'study', label: 'Study Leave', color: '#4caf50', maxDays: 30, description: 'Educational leave' },
    { value: 'compassionate', label: 'Compassionate Leave', color: '#795548', maxDays: 5, description: 'Bereavement/family emergency' },
  ];
  
  // Load current user from localStorage on mount
  useEffect(() => {
    loadUserData();
  }, []);
  
const loadUserData = async () => {
  setInitialLoading(true);
  try {
    // First check if userProfile exists (from your login)
    const userProfileStr = localStorage.getItem('userProfile');
    const authToken = localStorage.getItem('authToken');
    
    if (!authToken) {
      console.warn('No auth token found');
      showSnackbar('Please login to continue', 'warning');
      setInitialLoading(false);
      return;
    }
    
    if (userProfileStr) {
      const userProfile = JSON.parse(userProfileStr);
      console.log('User profile from login:', userProfile);
      
      // Map the user profile data to match what the component expects
      const userPhone = userProfile.phone || '';
      const userName = userProfile.full_name || 
                       `${userProfile.first_name || ''} ${userProfile.other_names || ''}`.trim() || 
                       'Employee';
      const userDept = userProfile.department || 'Not Assigned';
      const userEmail = userProfile.email || '';
      
      setCurrentUser({
        phone: userPhone,
        name: userName,
        department: userDept,
        email: userEmail,
        user_type: userProfile.user_type || 'employee'
      });
      
      setLeaveForm(prev => ({ ...prev, phone: userPhone }));
      
      // Store in individual keys for backward compatibility
      if (userPhone) localStorage.setItem('userPhone', userPhone);
      if (userName) localStorage.setItem('userName', userName);
      if (userDept) localStorage.setItem('userDepartment', userDept);
      if (userEmail) localStorage.setItem('userEmail', userEmail);
      
      console.log('User loaded from userProfile:', { phone: userPhone, name: userName });
    } else {
      // Fallback to individual keys (backward compatibility)
      const userPhone = localStorage.getItem('userPhone') || localStorage.getItem('phone') || localStorage.getItem('phoneNumber');
      const userName = localStorage.getItem('userName') || localStorage.getItem('full_name') || 'Employee';
      const userDept = localStorage.getItem('userDepartment') || localStorage.getItem('department') || '';
      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email');
      
      if (userPhone || userEmail) {
        setCurrentUser({
          phone: userPhone || '',
          name: userName,
          department: userDept,
          email: userEmail || '',
          user_type: 'employee'
        });
        setLeaveForm(prev => ({ ...prev, phone: userPhone || '' }));
      } else {
        console.warn('No user data found');
        showSnackbar('Please login to continue', 'warning');
      }
    }
    
    fetchDepartments();
  } catch (error) {
    console.error('Error loading user data:', error);
    showSnackbar('Error loading user data. Please refresh the page.', 'error');
  } finally {
    setInitialLoading(false);
  }
};  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message: message,
      severity: severity,
    });
  };
  
  useEffect(() => {
    if (currentUser.phone && !initialLoading) {
      console.log('Fetching leave data for phone:', currentUser.phone);
      fetchLeaveRequests();
      fetchLeaveStats();
    }
  }, [filters, page, currentUser.phone, initialLoading]);
  
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/hotel/HR/get_depts_list.php`);
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };
  
  const fetchLeaveRequests = async () => {
    if (!currentUser.phone) {
      console.warn('No phone number available to fetch leave requests');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.type !== 'all') params.append('leave_type', filters.type);
      if (filters.department !== 'all') params.append('department', filters.department);
      if (currentUser.phone && currentUser.user_type !== 'admin') {
        params.append('phone', currentUser.phone);
      }
      
      console.log('Fetching leave requests with params:', params.toString());
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/leave_requests.php?${params}`
      );
      
      if (response.data.success) {
        setLeaveRequests(response.data.data);
        console.log('Leave requests loaded:', response.data.data.length);
      } else {
        console.error('Error fetching leave requests:', response.data.message);
      }
    } catch (err) {
      console.error('Error fetching leave requests:', err);
      showSnackbar('Error fetching leave requests', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLeaveStats = async () => {
    if (!currentUser.phone) {
      console.warn('No phone number available to fetch stats');
      return;
    }
    
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/leave_stats.php?phone=${currentUser.phone}`;
      console.log('Fetching stats from:', url);
      
      const response = await axios.get(url);
      
      console.log('Stats API full response:', response.data);
      
      if (response.data.success && response.data.data) {
        const statsData = response.data.data;
        setLeaveStats({
          total: statsData.total || 0,
          pending: statsData.pending || 0,
          approved: statsData.approved || 0,
          rejected: statsData.rejected || 0,
          daysUsed: statsData.daysUsed || 0,
          daysRemaining: statsData.daysRemaining || 30,
        });
        console.log('Stats updated successfully:', statsData);
      } else {
        console.warn('No stats data received or success false, using defaults');
        setLeaveStats({
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
          daysUsed: 0,
          daysRemaining: 30,
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setLeaveStats({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        daysUsed: 0,
        daysRemaining: 30,
      });
    }
  };
  
  const getLeaveEvents = useCallback(() => {
    const events = [];
    leaveRequests.forEach(request => {
      if (request.status === 'approved' || request.status === 'pending') {
        events.push({
          ...request,
          start: parseISO(request.start_date),
          end: parseISO(request.end_date),
        });
      }
    });
    return events;
  }, [leaveRequests]);
  
  const getDaysInMonth = useCallback(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return eachDayOfInterval({ start, end });
  }, [currentDate]);
  
  const getEventsForDay = (day) => {
    const events = getLeaveEvents();
    return events.filter(event => 
      isWithinInterval(day, { start: event.start, end: event.end })
    );
  };
  
  const handleDateClick = (date) => {
    setSelectedDate(date);
    setOpenDialog(true);
    setLeaveForm({
      ...leaveForm,
      startDate: date,
      endDate: date,
    });
    setActiveStep(0);
    setFormErrors({});
  };
  
  const handlePrevMonth = () => {
    setCurrentDate(prev => subMonths(prev, 1));
  };
  
  const handleNextMonth = () => {
    setCurrentDate(prev => addMonths(prev, 1));
  };
  
  const handleToday = () => {
    setCurrentDate(new Date());
  };
  
  const validateDates = () => {
    const errors = {};
    const today = startOfDay(new Date());
    const startDate = normalizeDate(leaveForm.startDate);
    const endDate = normalizeDate(leaveForm.endDate);
    
    if (!startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (!endDate) {
      errors.endDate = 'End date is required';
    }
    
    if (startDate && endDate) {
      if (isAfter(startDate, endDate)) {
        errors.dates = 'End date must be after start date';
      }
      
      if (isBefore(startDate, today)) {
        errors.startDate = 'Start date cannot be in the past';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const calculateDuration = () => {
    if (!leaveForm.startDate || !leaveForm.endDate) return 0;
    const start = startOfDay(leaveForm.startDate);
    const end = startOfDay(leaveForm.endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };
  
  const handleSubmitLeave = async () => {
    console.log('Submitting leave request...', leaveForm);
    
    if (!currentUser.phone) {
      console.error('No phone number available for user');
      showSnackbar('User information missing. Please login again.', 'error');
      return;
    }
    
    const errors = {};
    if (!leaveForm.startDate) errors.startDate = 'Start date is required';
    if (!leaveForm.endDate) errors.endDate = 'End date is required';
    if (!leaveForm.reason || !leaveForm.reason.trim()) errors.reason = 'Reason is required';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }
    
    if (!validateDates()) {
      showSnackbar('Invalid date selection', 'error');
      return;
    }
    
    const selectedLeaveType = leaveTypes.find(t => t.value === leaveForm.leaveType);
    const duration = calculateDuration();
    
    if (selectedLeaveType?.maxDays && duration > selectedLeaveType.maxDays) {
      showSnackbar(`${selectedLeaveType.label} cannot exceed ${selectedLeaveType.maxDays} days. Current duration: ${duration} days.`, 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const startDate = format(startOfDay(leaveForm.startDate), 'yyyy-MM-dd');
      const endDate = format(startOfDay(leaveForm.endDate), 'yyyy-MM-dd');
      
      const requestData = {
        phone: currentUser.phone,
        leave_type: leaveForm.leaveType,
        start_date: startDate,
        end_date: endDate,
        reason: leaveForm.reason.trim(),
        contact_during_leave: leaveForm.contactDuringLeave || '',
        handover_notes: leaveForm.handoverNotes || ''
      };
      
      console.log('Sending request to backend:', requestData);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/leave_requests.php`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Response from backend:', response.data);
      
      if (response.data.success) {
        showSnackbar(`Leave request submitted successfully! (${duration} day${duration !== 1 ? 's' : ''})`, 'success');
        setOpenDialog(false);
        
        setLeaveForm({
          phone: currentUser.phone,
          leaveType: 'annual',
          startDate: null,
          endDate: null,
          reason: '',
          contactDuringLeave: '',
          handoverNotes: '',
        });
        setActiveStep(0);
        setFormErrors({});
        
        fetchLeaveRequests();
        fetchLeaveStats();
      } else {
        throw new Error(response.data.message || 'Failed to submit leave request');
      }
    } catch (err) {
      console.error('Error submitting leave:', err);
      
      let errorMessage = 'Error submitting leave request';
      
      if (err.response) {
        console.error('Response error data:', err.response.data);
        errorMessage = err.response.data?.message || err.response.data?.error || `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = err.message;
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleApproveLeave = async (leaveId) => {
    if (!window.confirm('Are you sure you want to approve this leave request?')) return;
    
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/leave_requests.php?id=${leaveId}`,
        {
          action: 'approve',
          approved_by: currentUser.name || 'HR Manager',
        }
      );
      
      if (response.data.success) {
        showSnackbar('Leave request approved successfully', 'success');
        fetchLeaveRequests();
        fetchLeaveStats();
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || 'Error approving leave', 'error');
    }
  };
  
  const handleRejectLeave = async (leaveId) => {
    const reason = prompt('Please provide reason for rejection:');
    if (!reason) return;
    
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/hotel/HR/leave_requests.php?id=${leaveId}`,
        {
          action: 'reject',
          approved_by: currentUser.name || 'HR Manager',
          rejection_reason: reason,
        }
      );
      
      if (response.data.success) {
        showSnackbar('Leave request rejected', 'warning');
        fetchLeaveRequests();
        fetchLeaveStats();
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || 'Error rejecting leave', 'error');
    }
  };
  
  const renderMonthView = () => {
    const days = getDaysInMonth();
    const startDay = getDay(startOfMonth(currentDate));
    const emptyDays = Array(startDay).fill(null);
    
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={handlePrevMonth}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ minWidth: 200, textAlign: 'center' }}>
              {format(currentDate, 'MMMM yyyy')}
            </Typography>
            <IconButton onClick={handleNextMonth}>
              <ArrowForward />
            </IconButton>
            <Button variant="outlined" onClick={handleToday} startIcon={<Today />}>
              Today
            </Button>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant={view === 'month' ? 'contained' : 'outlined'}
              onClick={() => setView('month')}
              startIcon={<CalendarToday />}
            >
              Month
            </Button>
            <Button
              variant={view === 'list' ? 'contained' : 'outlined'}
              onClick={() => setView('list')}
              startIcon={<ListAlt />}
            >
              List
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={1}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Grid item xs={1.71} key={day}>
              <Typography align="center" variant="subtitle2" fontWeight="bold">
                {day}
              </Typography>
            </Grid>
          ))}
          
          {emptyDays.map((_, index) => (
            <Grid item xs={1.71} key={`empty-${index}`}>
              <CalendarDay sx={{ visibility: 'hidden' }} />
            </Grid>
          ))}
          
          {days.map(day => {
            const events = getEventsForDay(day);
            const isToday = isSameDay(day, new Date());
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            
            return (
              <Grid item xs={1.71} key={day.toString()}>
                <CalendarDay
                  selected={isSelected}
                  hasEvent={events.length > 0}
                  isToday={isToday}
                  onClick={() => handleDateClick(day)}
                  sx={{ minHeight: 100 }}
                >
                  <Typography variant="body2" fontWeight={isToday ? 'bold' : 'normal'}>
                    {format(day, 'd')}
                  </Typography>
                  {events.length > 0 && (
                    <AvatarGroup max={3} sx={{ mt: 1, '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                      {events.slice(0, 3).map((event, idx) => (
                        <Tooltip key={idx} title={`${event.employee_name || 'Employee'} - ${event.leave_type}`}>
                          <Avatar sx={{ bgcolor: leaveTypes.find(t => t.value === event.leave_type)?.color }}>
                            {event.employee_name?.charAt(0) || 'E'}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </AvatarGroup>
                  )}
                </CalendarDay>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };
  
  const renderListView = () => {
    const filteredRequests = leaveRequests.filter(request => {
      if (filters.status !== 'all' && request.status !== filters.status) return false;
      if (filters.type !== 'all' && request.leave_type !== filters.type) return false;
      if (filters.search && !request.employee_name?.toLowerCase().includes(filters.search.toLowerCase())) return false;
      return true;
    });
    
    const paginatedRequests = filteredRequests.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5">Leave Requests</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search employee..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                displayEmpty
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                displayEmpty
              >
                <MenuItem value="all">All Types</MenuItem>
                {leaveTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {currentUser.user_type === 'admin' && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filters.department}
                  onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                  displayEmpty
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map(dept => (
                    <MenuItem key={dept.id} value={dept.departments}>{dept.departments}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchLeaveRequests}>
              Refresh
            </Button>
          </Box>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Leave Type</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                      No leave requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map(request => (
                  <TableRow key={request.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {request.employee_name?.charAt(0) || 'E'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {request.employee_name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {request.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{request.department || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={leaveTypes.find(t => t.value === request.leave_type)?.label || request.leave_type}
                        size="small"
                        sx={{ bgcolor: leaveTypes.find(t => t.value === request.leave_type)?.color || '#999', color: '#fff' }}
                      />
                    </TableCell>
                    <TableCell>{request.duration_days} days</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(parseISO(request.start_date), 'MMM dd')} - {format(parseISO(request.end_date), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={request.status.toUpperCase()}
                        status={request.status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {request.status === 'pending' && currentUser.user_type === 'admin' && (
                        <Box display="flex" gap={1}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApproveLeave(request.id)}
                            title="Approve"
                          >
                            <CheckCircle />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectLeave(request.id)}
                            title="Reject"
                          >
                            <Cancel />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {filteredRequests.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={Math.ceil(filteredRequests.length / rowsPerPage)}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Box>
    );
  };
  
  const renderStatistics = () => {
    const totalDays = (leaveStats.daysUsed || 0) + (leaveStats.daysRemaining || 0);
    const percentage = totalDays > 0 ? ((leaveStats.daysUsed || 0) / totalDays) * 100 : 0;
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h3" fontWeight="bold">
                {leaveStats.daysRemaining || 0}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Days Remaining
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={percentage} 
                sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: '#fff' } }} 
              />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                {leaveStats.daysUsed || 0} days used
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {leaveStats.pending || 0}
                </Typography>
                <EventNote sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Pending Requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {leaveStats.approved || 0}
                </Typography>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Approved Requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {leaveStats.rejected || 0}
                </Typography>
                <Cancel sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Rejected Requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };
  
  const renderNativeDateInputs = () => {
    const today = new Date().toISOString().split('T')[0];
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            type="date"
            label="Start Date"
            fullWidth
            value={leaveForm.startDate ? format(leaveForm.startDate, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value) : null;
              setLeaveForm({ ...leaveForm, startDate: date });
              if (formErrors.startDate) {
                setFormErrors({ ...formErrors, startDate: null });
              }
            }}
            error={!!formErrors.startDate}
            helperText={formErrors.startDate}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            type="date"
            label="End Date"
            fullWidth
            value={leaveForm.endDate ? format(leaveForm.endDate, 'yyyy-MM-dd') : ''}
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value) : null;
              setLeaveForm({ ...leaveForm, endDate: date });
              if (formErrors.endDate) {
                setFormErrors({ ...formErrors, endDate: null });
              }
            }}
            error={!!formErrors.endDate}
            helperText={formErrors.endDate}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Duration"
            value={`${calculateDuration()} day${calculateDuration() !== 1 ? 's' : ''}`}
            disabled
          />
        </Grid>
        {formErrors.dates && (
          <Grid item xs={12}>
            <FormHelperText error>{formErrors.dates}</FormHelperText>
          </Grid>
        )}
      </Grid>
    );
  };
  
  if (initialLoading) {
    return (
      <Backdrop open={true} sx={{ zIndex: 9999 }}>
        <CircularProgress color="primary" />
      </Backdrop>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f8f9fa' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1600px',
          mx: 'auto',
          width: '100%',
          overflow: 'auto',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.clear();
            window.location.href = '/';
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />
        
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Person color="primary" />
            <Typography variant="body1">
              <strong>{currentUser.name || 'Employee'}</strong> 
              {currentUser.department && ` - ${currentUser.department}`}
              {currentUser.phone && ` (${currentUser.phone})`}
            </Typography>
          </Box>
        </Paper>
        
        <Fade in timeout={500}>
          <Box sx={{ mt: 4 }}>
            {renderStatistics()}
            
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                bgcolor: '#fff',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                  Leave Calendar
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setSelectedDate(new Date());
                    setOpenDialog(true);
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    },
                  }}
                >
                  Request Leave
                </Button>
              </Box>
              
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {view === 'month' && renderMonthView()}
                  {view === 'list' && renderListView()}
                </>
              )}
            </Paper>
          </Box>
        </Fade>
      </Box>
      
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Zoom}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Request Leave</Typography>
            <IconButton onClick={() => setOpenDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Leave Details</StepLabel>
              {activeStep === 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Leave Type</InputLabel>
                        <Select
                          value={leaveForm.leaveType}
                          onChange={(e) => setLeaveForm({ ...leaveForm, leaveType: e.target.value })}
                          label="Leave Type"
                        >
                          {leaveTypes.map(type => (
                            <MenuItem key={type.value} value={type.value}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: type.color }} />
                                {type.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {renderNativeDateInputs()}
                  </Grid>
                </Box>
              )}
            </Step>
            
            <Step>
              <StepLabel>Reason & Details</StepLabel>
              {activeStep === 1 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Reason for Leave"
                        value={leaveForm.reason}
                        onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                        error={!!formErrors.reason}
                        helperText={formErrors.reason}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Contact During Leave"
                        placeholder="Phone or email"
                        value={leaveForm.contactDuringLeave}
                        onChange={(e) => setLeaveForm({ ...leaveForm, contactDuringLeave: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Handover To"
                        placeholder="Colleague's name"
                        value={leaveForm.handoverNotes}
                        onChange={(e) => setLeaveForm({ ...leaveForm, handoverNotes: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Step>
          </Stepper>
          
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button
              disabled={activeStep === 0}
              onClick={() => setActiveStep(prev => prev - 1)}
            >
              Back
            </Button>
            {activeStep === 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmitLeave}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => setActiveStep(prev => prev + 1)}
              >
                Continue
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveCalendar;